import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import type { StatusSlice } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Palette — warm earth tones consistent with CIEDA brand
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  pending: "#f59e0b",
  confirmed: "#d97706",
  processing: "#92400e",
  shipped: "#78716c",
  delivered: "#16a34a",
  cancelled: "#dc2626",
  returned: "#a8a29e",
};

const FALLBACK_COLORS = ["#d97706", "#f59e0b", "#92400e", "#78716c", "#a8a29e", "#16a34a", "#dc2626"];

function getColor(status: string, index: number): string {
  return STATUS_COLORS[status] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrdersByStatusProps {
  readonly data: ReadonlyArray<StatusSlice>;
  readonly isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrdersByStatus({ data, isLoading }: OrdersByStatusProps) {
  const { t } = useTranslation(["admin", "dashboard"]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
          {t("dashboard.charts.orders_by_status_title")}
        </h3>
        <div className="flex h-64 items-center justify-center">
          <div className="h-48 w-48 animate-pulse rounded-full bg-stone-100" />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
          {t("dashboard.charts.orders_by_status_title")}
        </h3>
        <div className="flex h-64 items-center justify-center text-sm text-stone-400">
          {t("dashboard.charts.no_data")}
        </div>
      </div>
    );
  }

  // Translate status labels for display
  const chartData = data.map((item) => ({
    ...item,
    label: t(`status.${item.status}`, { defaultValue: item.status }),
  }));

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-display text-lg font-semibold text-stone-900">
        {t("dashboard.charts.orders_by_status_title")}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[...chartData]}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.status}
                  fill={getColor(entry.status, index)}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [value, t("dashboard.charts.orders_label")]}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e7e5e4",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
